import moment from 'moment';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import { camelizeKeys } from 'humps';

import OrderItem from './OrderItem';

const ORDER_STATUSES = {
  0: 'starting...',
  1: 'Created',
  4: 'Completed the order',
  5: 'Reviewed and approved',
  6: 'Order delivered',
  7: 'Cancelled',
  8: 'Refund requested',
  9: 'Refunded',
  10: 'Partially refunded',
  11: 'Actual amount confirm',
  12: 'Card charged',
  13: 'Transfer completed'
};

class OrderDetails {
  static createOrderDetailsFromWarehouseResponse ({ whOrderDetails = {} } = {}) {
    const whOrderDetailsCamelized = camelizeKeys(whOrderDetails);
    const suborders = {};
    let retailerDiscount = 0;

    (whOrderDetailsCamelized.suborders || []).forEach(suborder => {
      let modsyDiscount = 0;
      const orderItems = suborder.orderitems.map(item => {
        const itemModelInstance = OrderItem.createOrderItemFromWarehouseResponse({
          whOrderItem: item
        });

        // the retailer discount from the backend is not 100% the retailer discount
        // it is a combination of modsy credits, retailer discount and modsy promo code
        // discount. To get true retailer discount, we will iterate through order items.
        // which gives the true retailer discount
        // TODO: Avoid this by changing back end to separate retailer discount value
        retailerDiscount += itemModelInstance.retailerDiscount() * itemModelInstance.quantity();
        modsyDiscount += itemModelInstance.modsyDiscount();

        return itemModelInstance;
      });

      suborders[suborder.id] = {
        ...suborder,
        orderItems,
        modsyDiscount
      };
    });

    return new OrderDetails({
      id: whOrderDetailsCamelized.id,
      estimate: whOrderDetailsCamelized.estimate,
      createdAt: whOrderDetailsCamelized.createdAt,
      couponCode: whOrderDetailsCamelized.couponCode,
      notesForCustomer: whOrderDetailsCamelized.notesForCustomer,
      insiderRewards: whOrderDetailsCamelized.tieredDiscount,
      // the value from warehouse is more than just a retailer discount
      groupedDiscount: whOrderDetailsCamelized.discount,
      modsyCredits: whOrderDetailsCamelized.creditsAppliedAmount,
      orderNumber: whOrderDetailsCamelized.orderNumber,
      orderStatus: whOrderDetailsCamelized.status,
      paymentDetails: whOrderDetailsCamelized.paymentDetails,
      retailerDiscount,
      shippingInfo: whOrderDetailsCamelized.shippingInfo,
      shippingFee: whOrderDetailsCamelized.shippingFee,
      suborders: suborders,
      subtotal: whOrderDetailsCamelized.subtotal,
      tag: whOrderDetailsCamelized.displayName || whOrderDetailsCamelized.tag,
      tax: whOrderDetailsCamelized.tax,

      // TODO: update this field name when it is implemented on the back end
      serviceFee: whOrderDetailsCamelized.serviceFee,
      total: whOrderDetailsCamelized.total,
      billingInfo: whOrderDetailsCamelized.billingInfo
    });
  }

  /** Creates a new order details instance with optional overrides. */
  constructor (overrides) {
    this.fields = { ...overrides };
  }

  /** Creates a new design copied from this with optional overrides. */
  makeCopy (overrides) {
    return new OrderDetails({ ...this.fields, ...overrides });
  }

  formatAddress (string) {
    // addresses are stored lowercase in BE. this doesn't handle
    // things like NE for northeast in an address
    return string ? string.split(' ').map(word => capitalize(word)).join(' ') : '';
  }

  formatCurrency (amount) {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  suborderSubtotal (suborder) {
    return this.formatCurrency(suborder.total - suborder.modsyDiscount);
  }

  // =======
  // GETTERS
  // =======
  /** Returns the id for this order. */
  id () { return this.fields.id; }

  addressCity (type) {
    return this.formatAddress(this.fields[`${type}Info`].city);
  }

  addressCityStateZip (type) {
    const city = this.addressCity(type);
    const state = this.addressState(type);
    const zipCode = this.addressZipCode(type);

    if (city && state && zipCode) {
      return `${this.addressCity(type)}, ${this.addressState(type)} ${this.addressZipCode(type)}`;
    } else {
      return null;
    }
  }

  addressName (type) {
    const addressInfo = this.fields[`${type}Info`];
    const nameArray = [];

    if (addressInfo.firstName) {
      nameArray.push(addressInfo.firstName);
    }

    if (addressInfo.lastName) {
      nameArray.push(addressInfo.lastName);
    }

    return this.formatAddress(nameArray.join(' '));
  }

  addressPhone (type) {
    const phone = get(this.fields[`${type}Info`], 'phone');
    if (phone) { return phone.toUpperCase(); }
  }

  addressState (type) {
    const state = get(this.fields[`${type}Info`], 'state');
    if (state) { return state.toUpperCase(); }
  }

  addressStreet (type) { return this.formatAddress(this.fields[`${type}Info`].street); }

  addressZipCode (type) { return this.fields[`${type}Info`].postal; }

  createdAt () { return this.fields.createdAt; }

  createdAtFormatted () {
    const date = moment(this.createdAt());
    return date.format('MMM D, YYYY');
  }

  customerNote () {
    const note = this.fields.notesForCustomer;

    return note.length === 0 ? null : note;
  }

  groupedDiscount () { return parseFloat(this.fields.groupedDiscount); }

  hasAnyAddressInfo (type) {
    const addressInfo = this.fields[`${type}Info`];
    if (!addressInfo) { return false; }

    return !!this.addressName(type) ||
      !!this.addressStreet(type) ||
      !!this.addressCityStateZip(type) ||
      !!this.addressPhone(type);
  }

  hasInsiderRewards () { return this.insiderRewards() < 0; }

  hasModsyCredits () { return this.modsyCredits() < 0; }

  hasPaymentInfo () { return this.paymentDetails() !== 'No payment info found'; }

  hasPromoCode () { return !!this.promoCode(); }

  insiderRewards () { return -parseFloat(this.fields.insiderRewards); }

  isFurnitureOrder () { return this.fields.tag === 'Furniture Order' || this.fields.tag === 'Product Purchase'; }

  isCreditCardOrder () {
    return !['Affirm', 'Paypal', 'No payment info found'].includes(this.paymentDetails());
  }

  modsyCredits () { return parseFloat(this.fields.modsyCredits); }

  orderNumber () { return this.fields.orderNumber; }

  orderStatus () { return this.fields.orderStatus; }

  orderStatusCopy () { return ORDER_STATUSES[this.orderStatus()]; }

  paymentDetails () { return this.fields.paymentDetails; }

  promoCode () { return this.fields.couponCode; }

  promoCodeDiscount () {
    return -(this.groupedDiscount() - this.modsyCredits() - this.retailerDiscount());
  }

  retailerDiscount () { return -parseFloat(this.fields.retailerDiscount); }

  shippingFee () { return parseFloat(this.fields.shippingFee); }

  suborders () { return this.fields.suborders; }

  subtotal () { return parseFloat(this.fields.subtotal) - this.retailerDiscount(); }

  tax () { return parseFloat(this.fields.tax); }

  fee () { return parseFloat(this.fields.serviceFee); }

  total () { return parseFloat(this.fields.total); }

  type () {
    switch (this.fields.tag) {
      case 'Product Purchase':
        return 'Product Order';
      case 'Furniture Order':
        return 'Product Order';
      default:
        return this.fields.tag;
    }
  }
}

export default OrderDetails;
